import React from 'react';
import { Howl } from 'howler';
import { SliceContext } from 'components/Slices/types';
import { prismicText } from 'helpers';
import { SegmentService } from 'analytics';
import { ABPlayersHowls, Versions, ABPlayersSources, Item } from '../types';
import { ABPlayersProgress } from './ABPlayersProgress';

export type ABPlayersProps = {
    sources: ABPlayersSources[];
    version: Versions;
    current: number;
    isPlaying: boolean;
    currentStyle: Item[];
    sliceContext: SliceContext;
    showSpinner: () => void;
    hideSpinner: () => void;
};

export type ABPlayersState = {
    players: ABPlayersHowls[];
};

const unselecteVersion = (selectedVersion: Versions) =>
    selectedVersion === Versions.mastered ? Versions.original : Versions.mastered;

const setMutes = (players: ABPlayersHowls[]) => (props: ABPlayersProps) => {
    players[props.current][unselecteVersion(props.version)].mute(true);
    players[props.current][props.version].mute(false);
};

const play = (players: ABPlayersHowls[]) => (props: ABPlayersProps) => {
    setMutes(players)(props);
    Object.values(players[props.current]).map((p) => p.play());

    const currentStyle = prismicText(props.currentStyle[props.current].mastering_demos_style);
    SegmentService.trackAudioPlayerLaunched(
        {
            'L - Destination Link': props.sources[props.current].original,
            'L - Asset Genre': currentStyle,
        },
        props.sliceContext,
    );
};

const pause = (players: ABPlayersHowls[]) =>
    players.forEach((song) => {
        song[Versions.mastered].pause();
        song[Versions.original].pause();
    });

const stopAll = (players: ABPlayersHowls[]) =>
    players.forEach((song) => {
        song[Versions.mastered].stop();
        song[Versions.original].stop();
    });

const createPlayers = (sources: ABPlayersSources[]) =>
    sources.map((s) => {
        return {
            [Versions.original]: new Howl({
                src: s[Versions.original],
                preload: false,
                loop: true,
            }),
            [Versions.mastered]: new Howl({
                src: s[Versions.mastered],
                preload: false,
                loop: true,
            }),
        };
    });

class ABPlayers extends React.PureComponent<ABPlayersProps, ABPlayersState> {
    // TODO: Refactor to remove useless state
    state = {
        players: createPlayers(this.props.sources),
    };
    songProgress: ABPlayersProgress;

    componentDidMount() {
        this.songProgress = new ABPlayersProgress(this.state.players[0]);
    }

    componentWillUnmount() {
        stopAll(this.state.players);
        this.songProgress.destroy();
    }

    componentDidUpdate(prevProps: ABPlayersProps) {
        if (this.props.isPlaying !== prevProps.isPlaying) {
            if (this.props.isPlaying) {
                if (this.state.players[this.props.current]['original'].state() !== 'loaded') {
                    this.props.showSpinner();
                } else {
                    play(this.state.players)(this.props);
                    this.songProgress.startProgress();
                }
                // Load stream
                Object.values(this.state.players[this.props.current]).map((p) => p.load());
                this.state.players[this.props.current][this.props.version].on('load', () => {
                    // Prevent to play a previous loaded stream if the user have change song during the loading process
                    if (this.props.current === prevProps.current) {
                        // Play the song when it's load
                        play(this.state.players)(this.props);
                        this.songProgress.startProgress();
                        this.props.hideSpinner();
                    }
                });
            } else {
                pause(this.state.players);
            }
        }

        if (this.props.version !== prevProps.version) {
            setMutes(this.state.players)(this.props);
            this.props.hideSpinner();
        }

        if (this.props.current !== prevProps.current) {
            stopAll(this.state.players);
            this.songProgress.clearProgress();
            this.songProgress.updateSource(this.state.players[this.props.current]);
            this.props.hideSpinner();
        }
    }

    render() {
        return null;
    }
}

export { ABPlayers };
